import { message } from 'antd'
import firebase from 'firebase/app'
import isEmpty from 'lodash/isEmpty'
import React, { Suspense, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import { DetailsLoader } from './components/DetailsLoader'
import DevRibbon from './components/DevRibbon'
import NoMatch from './components/NoMatch'
import { isDev } from './constants'
import { branchInit } from './helpers/branchHelper'
import { base } from './helpers/firebaseHelper'
import { initTracking, track } from './helpers/trackerHelper'
import withTracker from './hoc/withTracker'
import { $, key } from './localization/localization'
import PrivateRoute from './PrivateRoute'
import { toTktriesLang, useTktriesEmbedScript } from './hooks/use-tktries-embed-script'
import { settingsStore } from './stores/settingsStore'

let adblockerErrorDisplayed = false

const App = () => {
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [branchInitializing, setBranchInitializing] = useState(true)
  const language = settingsStore(state => state.language)
  const setLanguage = settingsStore(state => state.setLanguage)

  const hostOk =
    typeof window !== 'undefined' && window.location.hostname.endsWith('planify.io')
  useTktriesEmbedScript({
    apiKey: process.env.REACT_APP_TKTRIES_API_KEY,
    lang: toTktriesLang(language),
    enabled: hostOk,
  })

  useEffect(() => {
    setLanguage(language, false)
  }, [])

  useEffect(() => {
    initTracking()
  }, [])

  useEffect(() => {
    if (isMobile) {
      window.location.href = process.env.REACT_APP_MOBILE_URL
    }

    branchInit((error, data) => {
      if (error) {
        console.error(error)
        if (!adblockerErrorDisplayed) {
          message.error($(key.adblocker), 0)
          adblockerErrorDisplayed = true
        }
      }
      if (!data || !data.data_parsed) {
        return setBranchInitializing(false)
      }

      const dataParsed = data.data_parsed
      const invitationToken = dataParsed.invitationToken

      if (!isEmpty(invitationToken)) {
        localStorage.setItem('invitationToken', invitationToken)
      }

      const isFirstSession = dataParsed['+is_first_session']
      const sponsorId = dataParsed.sponsorId

      if (isFirstSession && !isEmpty(sponsorId)) {
        track('sponsored', { sponsorId })
        base.addToCollection('sponsored', {
          sponsorId: sponsorId,
          sponsoredId: firebase.auth().currentUser.uid,
        })
      }

      setBranchInitializing(false)
    })

    return firebase.auth().onAuthStateChanged(user => {
      if (user) {
        setCurrentUser(user)
        setLoading(false)
      } else {
        setCurrentUser(null)
        setLoading(false)
      }
    })
  }, [])

  if (loading || branchInitializing) {
    return <DetailsLoader />
  }

  const UpgradeOrPark = React.lazy(() => import('./hoc/pricing/UpgradeOrPark'))
  const UserProfile = React.lazy(() => import('./pages/Profile/UserProfile'))
  const Dashboard = React.lazy(() => import('./pages/Plans/PlansList'))
  const Plan = React.lazy(() => import('./pages/PlanDetails/Plan'))
  const Login = React.lazy(() => import('./pages/Login/Login'))

  const authenticated = currentUser !== null
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <Suspense fallback={<DetailsLoader />}>
        <Switch>
          <Route exact path='/login' component={withTracker(Login)} />
          <PrivateRoute
            exact
            path='/upgradeOrPark'
            component={withTracker(UpgradeOrPark)}
            authenticated={authenticated}
          />
          <PrivateRoute
            exact
            path='/'
            component={withTracker(Dashboard)}
            authenticated={authenticated}
          />
          <PrivateRoute
            path='/plans/:planId/:subResource?/:subResourceId?'
            component={withTracker(Plan)}
            authenticated={authenticated}
          />
          <PrivateRoute
            exact
            path='/userProfile'
            component={withTracker(UserProfile)}
            authenticated={authenticated}
          />
          <Route component={withTracker(NoMatch)} />
        </Switch>
        {isDev && <DevRibbon />}
      </Suspense>
    </BrowserRouter>
  )
}

export default App
